import { QueryCache, QueryClient } from '@tanstack/react-query';

import { ERROR_CLASS, classifyError } from './error-message.js';
import { ApiError, NetworkError } from './errors.js';

/**
 * The portal's query defaults, plus the backstop that stops a failure being
 * silently swallowed.
 */

const CLIENT_ERROR_FLOOR = 400;
const SERVER_ERROR_FLOOR = 500;
const MAX_RETRIES = 2;
const STALE_TIME_MS = 30_000;

/**
 * Whether a failed query is worth trying again.
 *
 * A 4xx is the server's considered answer: malformed request, missing
 * permission, deleted record. Retrying produces the same answer three times,
 * delays the error the user needs, and spends three requests against the
 * Gateway's rate limiter — which can turn one forbidden request into a block
 * on everything else that user does.
 *
 * A 401 is never retried here. `ApiClient` has already attempted a refresh
 * and given up by the time the error reaches this layer, and a refresh storm
 * is what makes the auth service treat a reused refresh token as theft and
 * revoke the whole family.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) return false;
  if (error instanceof NetworkError) return true;

  if (error instanceof ApiError) {
    return error.status >= SERVER_ERROR_FLOOR || error.status < CLIENT_ERROR_FLOOR;
  }

  return false;
}

/**
 * Whether a failure is worth announcing (via a toast, typically).
 *
 * Every class except FIELD is announced: a query failure is frequently
 * invisible otherwise — a background refetch that 403s or times out leaves
 * the screen showing the rows it already had, with nothing to say the data
 * is now stale and unauthorised.
 *
 * `hasData` is accepted for signature stability and to make the caller's
 * intent explicit at the call site, but does not currently change the
 * decision — every classified error except FIELD is announced regardless of
 * whether the query already has cached data, because even a query with no
 * data deserves the same announcement.
 */
export function shouldAnnounce(error: unknown, hasData: boolean): boolean {
  void hasData;
  return classifyError(error) !== ERROR_CLASS.FIELD;
}

export interface QueryClientOptions {
  /**
   * Called for every failure worth announcing.
   *
   * Wired to a toast in `App.tsx`. Everything except a field validation
   * failure reaches it.
   */
  readonly onError?: (error: unknown) => void;
}

export function createQueryClient(options: QueryClientOptions = {}): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        if (shouldAnnounce(error, false)) options.onError?.(error);
      },
    }),
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        staleTime: STALE_TIME_MS,
        // Admin screens sit open on a second monitor for hours. Refetching on
        // focus is how a stale rate plan stops being edited from yesterday.
        refetchOnWindowFocus: true,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
